/**
 * Protected Route Component
 * @implements TS-022 - Role-based Routing
 *
 * Wrapper component that checks authentication and optional role requirements.
 * Redirects unauthenticated users to login while preserving the return URL.
 */

import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useAuthContext } from '@/providers';
import { UserRole } from '@/types';

/**
 * Props for the ProtectedRoute component
 */
export interface ProtectedRouteProps {
  /** The content to render if authenticated */
  children: ReactNode;
  /** Optional role required to access this route */
  requiredRole?: UserRole;
}

/**
 * ProtectedRoute - Guards routes requiring authentication
 *
 * Features:
 * - Checks authentication state via useAuthContext
 * - Shows loading spinner during auth check
 * - Redirects to /login if not authenticated (preserves returnUrl)
 * - Optionally checks for required role
 *
 * @implements TS-022.1 - ProtectedRoute component
 *
 * @example
 * // Basic protected route
 * <Route path="/dashboard" element={
 *   <ProtectedRoute>
 *     <DashboardPage />
 *   </ProtectedRoute>
 * } />
 *
 * @example
 * // Role-protected route
 * <Route path="/dashboard/po" element={
 *   <ProtectedRoute requiredRole="PRODUCT_OWNER">
 *     <PODashboardPage />
 *   </ProtectedRoute>
 * } />
 */
export const ProtectedRoute = ({
  children,
  requiredRole,
}: ProtectedRouteProps) => {
  const { isLoading, isAuthenticated, user } = useAuthContext();
  const location = useLocation();

  // Show loading state during initial auth check
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          gap: 2,
        }}
      >
        <CircularProgress size={48} />
        <Typography color="text.secondary">
          Checking authentication...
        </Typography>
      </Box>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    // Preserve the current URL as returnUrl for post-login redirect
    const returnUrl = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?returnUrl=${returnUrl}`} replace />;
  }

  // Check for required role if specified
  if (requiredRole && user) {
    // Compare roles case-insensitively since backend returns lowercase (product_owner)
    // while GraphQL enum uses uppercase (PRODUCT_OWNER)
    const requiredRoleLower = requiredRole.toLowerCase();
    const hasRequiredRole = user.roles.some(
      (role) => role.toLowerCase() === requiredRoleLower,
    );

    if (!hasRequiredRole) {
      // User is authenticated but lacks the required role
      // Redirect to their primary dashboard instead
      return <Navigate to="/" replace />;
    }
  }

  // User is authenticated and has required role (if any)
  return <>{children}</>;
};
